import classNames from 'classnames';
import { FieldProps } from 'formik';
import { FC } from 'react';
import { HiMinusSm, HiCheck } from 'react-icons/hi';
import { ILabelTitleSubtitle, LabelTitleSubtitle } from './LabelTitleSubtitle';

export interface ICheckboxV2 {
  onChange: (checked: boolean) => void;
  isChecked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
}

export const CheckboxV2: FC<ICheckboxV2> = (props) => {
  const { onChange, isChecked, indeterminate, disabled } = props;

  const Icon = indeterminate ? HiMinusSm : HiCheck;
  const isMark = isChecked;

  return (
    <div
      className={classNames('relative w-fit', {
        'pointer-events-none': disabled,
      })}
    >
      {/* Invisible input is add so that we can wrap in <label> that provide bigger hit area */}
      <input
        type="checkbox"
        className="absolute left-0 top-0 h-full w-full opacity-0"
        onChange={() => onChange(!isChecked)}
        checked={isChecked}
      />
      <div
        className={classNames(
          'relative flex h-4 w-4 cursor-pointer items-center justify-center rounded text-transparent',
          isMark
            ? 'bg-grayv2-700 hover:border hover:border-grayv2-900 focus:border focus:border-grayv2-900 '
            : 'border border-grayv2-200 hover:border-grayv2-500 focus:border-grayv2-900 ',
          disabled && isMark
            ? 'border-grayv2-300 bg-grayv2-300'
            : 'border-grayv2-300',
        )}
      >
        <Icon className={classNames('h-3 w-3', { 'text-white': isMark })} />
      </div>
    </div>
  );
};

/**
 * If you are dealing with multiple checkboxes that belong to the same group, please assign associated value to `assignValue` param.
 * Otherwise, don't pass anything to `assignValue` param.
 *
 * For usage example, please check storybook
 */
export function buildPropsWithFormikSupport(
  fieldProps: FieldProps,
  assignValue?: string,
) {
  const { value, name, onChange } = fieldProps.field;
  const outValue = assignValue || value;

  return {
    isChecked: Array.isArray(value) ? value.includes(assignValue) : outValue,
    onChange: (isChecked: boolean) =>
      onChange({
        target: {
          name,
          checked: isChecked,
          value: outValue,
          type: 'checkbox',
        },
      }),
  };
}

interface CheckBoxWithLabel {
  labelProps: ILabelTitleSubtitle;
  checkboxV2Props: ICheckboxV2;
  disabled?: boolean;
}
export const CheckBoxWithLabel: FC<CheckBoxWithLabel> = (props) => {
  const { labelProps, checkboxV2Props, disabled } = props;

  return (
    <label
      className={classNames(
        'flex cursor-pointer space-x-3',
        {
          'pointer-events-none': disabled,
        },
        labelProps.subtitle ? 'items-baseline' : 'items-center',
      )}
    >
      <CheckboxV2 {...checkboxV2Props} disabled={disabled} />
      <LabelTitleSubtitle {...labelProps} disabled={disabled} />
    </label>
  );
};
